/**
 * FILE: EnrollmentModal.js
 * PURPOSE: The "New Member Onboarding" window (Digital Ghost Flow).
 *
 * STATE MACHINE: navigation is driven by two numbers rather than a router:
 *   `step`       — the outer stage: 0 gateway (social/email choice), 1 email entry,
 *                  2 OTP entry, 3 demographic trait wizard, 4 finalizing spinner,
 *                  6 success screen, 7 terms + "slide to start" finalize screen,
 *                  10 "Discovery Hub" (shown when VANE pre-flight disqualifies the user
 *                  from the poll they were trying to vote on, offering alternatives).
 *   `wizardStep` — sub-step within `step === 3` only (1 age, 2 gender, 3 education,
 *                  4 work status, 5 industry — 5 is skipped unless workStatus === 'Working').
 * `handleBack()` and `skipStep()` encode the few legal transitions between these by hand;
 * there's no formal state chart, so changing the step numbers requires updating every
 * conditional that references them (renderStep* functions, the progress tracker's `[0,1,2,3,7]`
 * array, handleBack, skipStep, selectTraitAndAdvance).
 *
 * The final `step === 7` screen's drag-to-confirm slider is the shared <SlideToConfirm>
 * component (components/SlideToConfirm.js) — also used by PollCard for casting a vote.
 */
import React, {
    useState
} from 'react';
import {
    X, Mail, Lock, Shield, ShieldCheck, Fingerprint, Sparkles, Loader2,
    AlertCircle, ArrowRight, Ghost, Facebook, Coins, ArrowLeft,
    Zap, Activity, Sun, Home, ChevronRight, Briefcase, Heart, BookOpen,
    Scale, Pickaxe, Tractor, ZapOff, HardHat, Plane, ShoppingBag, Globe,
    ShieldAlert
} from 'lucide-react';
import { api } from '../utils/api';
import { useGoogleLogin } from '@react-oauth/google';
import SlideToConfirm from './SlideToConfirm';
import { useModalA11y } from '../hooks/useModalA11y';

export function EnrollmentModal({ onComplete, onClose, pollId }) {
    const containerRef = useModalA11y(true, onClose);
    const [step, setStep] = useState(0);
    const [wizardStep, setWizardStep] = useState(1);
    const [discoveryPolls, setDiscoveryPolls] = useState([]);

    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [anonymousId, setAnonymousId] = useState(localStorage.getItem('ghost_id') || '');
    const [traits, setTraits] = useState({
        age: '',
        gender: '',
        education: '',
        workStatus: '',
        sector: '',
        region: ''
    });

    const [loading, setLoading] = useState(false);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [error, setError] = useState(null);
    const [backendMsg, setBackendMsg] = useState(null);
    const [coins, setCoins] = useState(0);

    const [isSealing, setIsSealing] = useState(false);
    const [geoLabel, setGeoLabel] = useState(null);
    const [selectingId, setSelectingId] = useState(null);
    // Starts unchecked — a pre-ticked consent checkbox is a dark pattern regardless of intent.
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const handleSlideConfirm = () => {
        setShowConfetti(true);
        if (navigator.vibrate) navigator.vibrate([40, 30, 80]);
        activateAndCast();
    };

    function handleBack() {
        if (isAdvancing) return;
        if (step === 1) handleGatewayReset();
        else if (step === 2) setStep(1);
        else if (step === 3) {
            if (wizardStep > 1) {
                if (wizardStep === 5 && traits.workStatus !== 'Working') setWizardStep(4);
                else setWizardStep(prev => prev - 1);
            } else handleGatewayReset();
        } else if (step === 7) {
            setStep(3);
            setWizardStep(traits.workStatus === 'Working' ? 5 : 4);
        }
    }

    function handleGatewayReset() {
        setStep(0);
        setError(null);
        setBackendMsg(null);
    }

    const handleSocialHandshake = async (p, token) => {
        setStep(4);
        setBackendMsg(`Securing Digital Ghost identity...`);
        try {
            const handshakeRes = await api.socialHandshake({
                token: token,
                provider: p
            });

            setAnonymousId(handshakeRes.anonymousId);
            setCoins(handshakeRes.coins || 0);
            localStorage.setItem('ghost_id', handshakeRes.anonymousId);
            localStorage.setItem('ghost_token', handshakeRes.token);

            if (handshakeRes.resumeStep) {
                setStep(3);
                setWizardStep(handshakeRes.resumeStep);
            } else if (handshakeRes.isNewUser) {
                setStep(3);
                setWizardStep(1);
            } else {
                onComplete({ 
                    token: handshakeRes.token, 
                    anonymousId: handshakeRes.anonymousId,
                    email: 'social-identity' 
                });
            }
        } catch (err) {
            setError(err.message);
            setStep(0);
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = useGoogleLogin({
        onSuccess: tokenResponse => handleSocialHandshake('google', tokenResponse.access_token),
        onError: () => {
            setError("Google Authentication Failed");
            setStep(0);
        }
    });

    const handleSocialChoice = (p) => {
        if (p === 'google') {
            googleLogin();
        }
        // Facebook sign-in has no App ID configured yet — see the disabled button below.
    };

    const handleRequestOTP = async (e) => {
        e?.preventDefault();
        setLoading(true);
        setError(null);
        try {
            // STEP A: Pre-flight check
            const res = await api.preflight(email, pollId);
            
            if (res.status === 'CALIBRATION_REQUIRED') {
                // New User: Prompt for age/gender BEFORE OTP to build identity
                setStep(3);
                setWizardStep(1); 
            } else if (res.status === 'DISQUALIFIED') {
                // Not qualified for THIS poll -> Discovery Hub
                setDiscoveryPolls(res.eligible_polls || []);
                setStep(10); 
            } else {
                // Qualified or General Signup -> Proceed to OTP
                await api.requestOTP(email);
                setStep(2);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async (manualOtp) => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.verifyOTP(email, manualOtp || otp);
            setAnonymousId(res.anonymousId);
            setCoins(res.coins || 0);
            localStorage.setItem('ghost_id', res.anonymousId);
            localStorage.setItem('ghost_token', res.token);

            if (res.resumeStep) {
                setStep(3);
                setWizardStep(res.resumeStep);
            } else if (res.isNewUser) {
                setStep(3);
                setWizardStep(1);
            } else {
                onComplete({ token: res.token, anonymousId: res.anonymousId, email: email });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const selectTraitAndAdvance = async (key, value) => {
        if (isAdvancing) return;

        if (traits[key] === value) {
            setTraits(prev => ({ ...prev, [key]: '' }));
            return;
        }

        setSelectingId(value);
        setTraits(prev => ({ ...prev, [key]: value }));
        setIsAdvancing(true);

        await new Promise(r => setTimeout(r, 400));

        try {
            const res = await api.updateTraits(anonymousId, { [key]: value });
            if (res.coins !== undefined) setCoins(res.coins);
            if (res.geoResult) setGeoLabel(res.geoResult.label);

            let nextStep = 3;
            let nextWizardStep = wizardStep;

            if (wizardStep === 1) nextWizardStep = 2;
            else if (wizardStep === 2) nextWizardStep = 3;
            else if (wizardStep === 3) nextWizardStep = 4;
            else if (wizardStep === 4) {
                if (value === 'Working') nextWizardStep = 5;
                else { nextStep = 7; }
            } else if (wizardStep === 5) { nextStep = 7; }

            if (nextStep === 7) {
                // If they came from Pre-flight calibration, we need to request OTP now
                // rather than sealing immediately.
                if (step === 3 && wizardStep <= 2 && !anonymousId) {
                    setLoading(true);
                    try {
                        await api.requestOTP(email);
                        setStep(2);
                    } catch (err) {
                        setError(err.message);
                        setStep(1);
                    } finally {
                        setLoading(false);
                    }
                } else {
                    setStep(7);
                }
            } else {
                setWizardStep(nextWizardStep);
            }
            setIsAdvancing(false);
            setSelectingId(null);
        } catch (err) {
            setIsAdvancing(false);
            setSelectingId(null);
        }
    };

    const skipStep = () => {
        if (wizardStep === 3) setWizardStep(4);
        else if (wizardStep === 4) setStep(7);
        else if (wizardStep === 5) setStep(7);
    };

    // Final step of the onboarding wizard: promotes the Ghost identity to ACTIVE status and
    // grants the welcome bonus. Reads the token back out of localStorage (rather than from a
    // local variable) because it was already written there by handleVerifyOTP /
    // handleSocialHandshake earlier in the flow, before the trait wizard ever ran — this call
    // relies on that earlier write having happened.
    const activateAndCast = async () => {
        if (!acceptedTerms) {
            setError("Please accept the Terms to continue.");
            return;
        }
        setLoading(true);
        setIsSealing(true);
        try {
            const res = await api.finalizeGhost(anonymousId);
            setCoins(res.coins);
            setTimeout(() => {
                setStep(6);
                onComplete({ token: localStorage.getItem('ghost_token'), anonymousId, email, isNewUser: true });
                setIsSealing(false);
                setLoading(false);
            }, 800);
        } catch (err) {
            setError("Check your connection and try again.");
            setIsSealing(false);
            setLoading(false);
        }
    };

    const ageMap = {
        '18-24': 'Gen Z',
        '25-34': 'Millennial',
        '35-44': 'Gen X',
        '45-54': 'Baby Boomer',
        '55+': 'Seniors'
    };

    const industries = [
        { id: 'Tech', name: 'Software/IT' },
        { id: 'Finance', name: 'Banking' },
        { id: 'Health', name: 'Health Care' },
        { id: 'Edu', name: 'Education' },
        { id: 'Gvt', name: 'Government' },
        { id: 'Retail', name: 'Retail' },
        { id: 'Const', name: 'Construction' },
        { id: 'Agri', name: 'Agriculture' },
        { id: 'Crypto', name: 'Web3 & Crypto' },
        { id: 'Transp', name: 'Logistics' },
        { id: 'Manuf', name: 'Manufacturing' },
        { id: 'Other', name: 'Others' }
    ];

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-2 sm:p-4">
            <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl animate-in fade-in duration-500" onClick={onClose}></div>

            <div ref={containerRef} role="dialog" aria-modal="true" aria-label="Create your Ghost identity" className="relative w-full max-w-4xl bg-white dark:bg-gray-950 sm:rounded-modal shadow-[0_0_150px_rgba(124,58,237,0.2)] animate-in zoom-in-95 duration-700 border border-gray-100 dark:border-white/5 flex flex-col overflow-hidden min-h-[500px] sm:min-h-[560px] h-[96vh] sm:max-h-[90vh]">

                {/* PROGRESS TRACKER */}
                <div className="px-6 pt-5 pb-2 relative shrink-0">
                    <div className="flex items-center gap-1.5 mb-3 justify-center">
                        {[0, 1, 2, 3, 7].map((s, idx) => (
                            <div key={s} className="flex items-center">
                                <div className={`h-1.5 w-10 sm:w-20 rounded-full transition-all duration-700 ${step >= s ? 'bg-primary-600' : 'bg-gray-100 dark:bg-gray-800'}`}></div>
                                {idx < 4 && <div className="w-1.5 h-1.5 rounded-full mx-1 bg-gray-200 dark:bg-gray-700"></div>}
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between px-2">
                        <button onClick={handleBack} aria-label="Go back" className={`p-2.5 hover:bg-gray-100 dark:hover:bg-white/5 rounded-2xl transition-all text-gray-500 z-50 ${(step === 0 || step === 4 || step === 6) ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                            <ArrowLeft size={22} />
                        </button>
                        <div className="text-center flex-1">
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-black italic uppercase tracking-tighter text-gray-900 dark:text-white leading-none">
                                {step === 0 && "Welcome Aboard"}
                                {step === 1 && "Basic Entry"}
                                {step === 2 && "Quick Check"}
                                {step === 3 && `Profile Step ${wizardStep}/5`}
                                {step === 4 && "Finalizing..."}
                                {step === 7 && "Ready to Start"}
                                {step === 10 && "Discovery Hub"}
                                {step === 6 && "Welcome Home"}
                            </h2>
                        </div>
                        <button onClick={onClose} aria-label="Close" className="p-2.5 hover:bg-gray-100 dark:hover:bg-white/5 rounded-2xl transition-all text-gray-500 z-50">
                            <X size={22} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar px-6 sm:px-12 md:px-16 py-4 flex flex-col justify-center">
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-500 text-xs font-bold animate-pulse justify-center">
                            <AlertCircle size={18} /> {error}
                        </div>
                    )}

                    {/* STEP 0: GATEWAY */}
                    {step === 0 && (
                        <div className="grid md:grid-cols-2 gap-8 items-center animate-in fade-in zoom-in-95 duration-700 h-full py-4 text-center md:text-left">
                            <div className="space-y-5">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-400 text-black text-[11px] font-black uppercase rounded-full shadow-lg border-2 border-white dark:border-zinc-900 animate-bounce">
                                    <Coins size={14} fill="currentColor" /> Claim 50 Coins Reward
                                </div>
                                <h3 className="text-4xl lg:text-5xl font-black italic uppercase tracking-tighter text-gray-900 dark:text-white leading-[0.9]">
                                    Your Voice <br /><span className="text-primary-600">Starts Here.</span>
                                </h3>
                                <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 space-y-2">
                                    <p className="text-gray-500 dark:text-gray-400 font-bold italic text-sm leading-relaxed">
                                        Your data is <span className="text-green-500 underline decoration-2 underline-offset-2">100% Anonymous</span>.
                                    </p>
                                    <p className="text-[11px] text-gray-500 font-black uppercase tracking-wider leading-none">
                                        Hidden from the world and even from us.
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <button 
                                    onClick={() => handleSocialChoice('google')} 
                                    className="group w-full flex items-center justify-between p-4 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl hover:border-red-500 transition-all shadow-sm"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-2.5 shadow-inner border border-gray-100">
                                            <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="G" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="text-left font-black text-sm uppercase">Sign In with Google</div>
                                    </div>
                                    <ArrowRight className="text-gray-300 group-hover:text-red-500" size={20} />
                                </button>

                                <button
                                    type="button"
                                    disabled
                                    aria-disabled="true"
                                    title="Facebook sign-in isn't available yet"
                                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-white/[0.02] border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl cursor-not-allowed opacity-60"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-gray-200 dark:bg-white/10 rounded-xl flex items-center justify-center p-2.5">
                                            <Facebook size={26} className="text-gray-500" />
                                        </div>
                                        <div className="text-left font-black text-sm uppercase text-gray-500">Sign In with Facebook</div>
                                    </div>
                                    <span className="text-2xs font-black uppercase tracking-widest text-gray-500 bg-gray-200 dark:bg-white/10 px-2 py-1 rounded-md">Coming Soon</span>
                                </button>
                                <div className="text-center text-2xs font-black uppercase text-gray-300 py-2 tracking-[6px]">or</div>
                                <button onClick={() => setStep(1)} className="group w-full flex items-center justify-between p-4 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl hover:border-primary-600 transition-all shadow-sm">
                                    <div className="flex items-center gap-4 font-black text-sm uppercase">
                                        <div className="w-12 h-12 bg-gray-50 dark:bg-white/10 rounded-xl flex items-center justify-center">
                                            <Mail className="text-gray-500" size={22} />
                                        </div>
                                        Sign In with Email
                                    </div>
                                    <ArrowRight className="text-gray-300 group-hover:text-primary-600" size={20} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* STEP 1: EMAIL */}
                    {step === 1 && (
                        <div className="max-w-md mx-auto py-6 space-y-6 animate-in fade-in text-center">
                            <div className="w-20 h-20 bg-primary-600/10 rounded-3xl flex items-center justify-center mx-auto mb-4"><Mail className="text-primary-600" size={36} /></div>
                            <h3 className="text-4xl font-black italic uppercase tracking-tighter text-gray-900 dark:text-white leading-tight">Enter your Email?</h3>
                            <form onSubmit={handleRequestOTP} className="space-y-4">
                                <input required type="email" placeholder="ghost@network.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-16 bg-gray-50 dark:bg-white/5 border-2 border-transparent focus:border-primary-600 rounded-2xl px-6 outline-none font-black text-xl text-center shadow-inner dark:text-white" />
                                <button disabled={loading || !email} type="submit" className="w-full py-5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-black text-lg shadow-lg uppercase italic flex items-center justify-center gap-3 transition-colors">
                                    {loading ? <Loader2 className="animate-spin" /> : <>Get Verification Code <ArrowRight size={22} /></>}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* STEP 2: OTP */}
                    {step === 2 && (
                        <div className="max-w-md mx-auto py-6 space-y-8 animate-in fade-in text-center">
                            <div className="p-8 bg-blue-500/5 border border-blue-500/10 rounded-3xl space-y-2">
                                <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto"><Lock className="text-blue-500" size={28} /></div>
                                <p className="text-gray-500 font-bold italic text-base">Check your email for the code.</p>
                                <p className="text-gray-900 dark:text-white font-black uppercase text-xl">{email}</p>
                            </div>
                            <input required maxLength={4} type="text" inputMode="numeric" placeholder="0000" value={otp} autoFocus onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                setOtp(val);
                                if (val.length === 4) handleVerifyOTP(val);
                            }} className="w-full h-20 bg-gray-50 dark:bg-white/5 border-2 border-transparent focus:border-blue-500 rounded-3xl px-6 outline-none font-black text-5xl text-center tracking-[0.6em] dark:text-white shadow-inner" />
                        </div>
                    )}

                    {/* STEP 3: WIZARD */}
                    {step === 3 && (
                        <div className="w-3/4 mx-auto py-2 space-y-5 animate-in fade-in" >
                            {/* BOUNTY CALLOUT */}
                            {[2, 3, 4, 5].includes(wizardStep) && (
                                <div className="flex items-center justify-center">
                                    <div className="px-5 py-2 bg-amber-400 text-black text-[12px] font-black uppercase rounded-full shadow-2xl flex items-center gap-2 animate-bounce border-2 border-white dark:border-zinc-900">
                                        <Sparkles size={14} fill="currentColor" /> 🎁 Get +10 Bonus for this info
                                    </div>
                                </div>
                            )}

                            {/* AGE */}
                            {wizardStep === 1 && (
                                <div className="space-y-4 text-center animate-in">
                                    <p className="text-gray-700 dark:text-gray-300 font-black italic text-xl sm:text-2xl uppercase tracking-tighter">How old are you?</p>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                                        {['18-24', '25-34', '35-44', '45-54', '55+'].map(a => (
                                            <button key={a} disabled={isAdvancing && selectingId !== a} onClick={() => selectTraitAndAdvance('age', a)} className={`h-32 sm:h-36 rounded-[40px] text-2xl font-black transition-all border-4 flex flex-col items-center justify-center bg-gray-50 dark:bg-white/5 group relative overflow-hidden ${traits.age === a ? 'border-primary-600 bg-primary-600/20 shadow-[0_0_50px_rgba(124,58,237,0.3)] scale-[1.02]' : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-primary-600/40 hover:scale-[1.02]'} ${selectingId === a ? 'scale-[1.1] z-10' : ''}`}>
                                                <span className="text-[11px] uppercase tracking-widest font-black opacity-60 mb-2">{ageMap[a]}</span>
                                                {a}
                                                {traits.age === a && <div className="absolute top-3 right-4"><Sparkles className="text-primary-600 animate-pulse" size={20} /></div>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* GENDER */}
                            {wizardStep === 2 && (
                                <div className="space-y-4 text-center animate-in">
                                    <p className="text-gray-700 dark:text-gray-300 font-black italic text-xl sm:text-2xl uppercase tracking-tighter">What is your Gender?</p>
                                    <div className="grid grid-cols-2 gap-6 max-w-xl mx-auto">
                                        {['Male', 'Female', 'Non-Binary', 'Private'].map(g => (
                                            <button key={g} disabled={isAdvancing && selectingId !== g} onClick={() => selectTraitAndAdvance('gender', g)} className={`h-32 sm:h-36 rounded-[40px] text-3xl font-black transition-all border-4 flex flex-col items-center justify-center bg-gray-50 dark:bg-white/5 relative overflow-hidden ${traits.gender === g ? 'border-pink-500 bg-pink-500/20 shadow-[0_0_50px_rgba(236,72,153,0.3)] scale-[1.02]' : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-pink-500/40 hover:scale-[1.02]'} ${selectingId === g ? 'scale-[1.1] z-10' : ''}`}>
                                                {g}
                                                {traits.gender === g && <div className="absolute top-3 right-4"><Sparkles className="text-pink-500 animate-pulse" size={20} /></div>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* EDUCATION (Degree) */}
                            {wizardStep === 3 && (
                                <div className="space-y-4 text-center animate-in">
                                    <p className="text-gray-700 dark:text-gray-300 font-black italic text-xl sm:text-2xl uppercase tracking-tighter">What is your education level?</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                                        {['High School', 'University', 'Post-Grad', 'Self-Taught'].map(lvl => (
                                            <button key={lvl} disabled={isAdvancing && selectingId !== lvl} onClick={() => selectTraitAndAdvance('education', lvl)} className={`h-32 sm:h-36 rounded-[40px] text-2xl font-black transition-all border-4 flex items-center px-10 justify-center bg-gray-50 dark:bg-white/5 relative overflow-hidden ${traits.education === lvl ? 'border-blue-500 bg-blue-500/20 shadow-[0_0_50px_rgba(59,130,246,0.3)]' : 'border-transparent text-gray-600 dark:text-gray-400 hover:border-blue-500/40 hover:scale-[1.01]'} ${selectingId === lvl ? 'scale-[1.05] z-10' : ''}`}>
                                                {lvl}
                                                {traits.education === lvl && <div className="absolute top-3 right-4"><Sparkles className="text-blue-500 animate-pulse" size={30} /></div>}
                                            </button>
                                        ))}
                                    </div>
                                    <button onClick={skipStep} className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all mt-4 group">
                                        Skip this step <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
                                    </button>
                                </div>
                            )}

                            {/* STATUS (Working or not) */}
                            {wizardStep === 4 && (
                                <div className="space-y-4 text-center animate-in">
                                    <p className="text-gray-700 dark:text-gray-300 font-black italic text-xl sm:text-2xl uppercase tracking-tighter">What is your work status?</p>
                                    <div className="grid grid-cols-2 gap-6 max-w-2xl mx-auto">
                                        {['Working', 'Student', 'Retired', 'Other'].map(s => (
                                            <button key={s} disabled={isAdvancing && selectingId !== s} onClick={() => selectTraitAndAdvance('workStatus', s)} className={`h-32 sm:h-36 rounded-[40px] text-2xl font-black transition-all border-4 flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-white/5 relative overflow-hidden ${traits.workStatus === s ? 'border-emerald-500 bg-emerald-500/20 shadow-[0_0_50px_rgba(16,185,129,0.3)] scale-[1.02]' : 'border-transparent text-gray-600 dark:text-gray-400 hover:border-emerald-500/40 hover:scale-[1.02]'} ${selectingId === s ? 'scale-[1.1] z-10' : ''}`}>
                                                {s}
                                                {traits.workStatus === s && <div className="absolute top-3 right-4"><Sparkles className="text-emerald-500 animate-pulse" size={20} /></div>}
                                            </button>
                                        ))}
                                    </div>
                                    <button onClick={skipStep} className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all mt-4 group">
                                        Skip this step <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
                                    </button>
                                </div>
                            )}

                            {/* SECTOR (Industry) */}
                            {wizardStep === 5 && (
                                <div className="space-y-4 text-center animate-in py-2">
                                    <p className="text-gray-700 dark:text-gray-300 font-black italic text-lg sm:text-xl uppercase tracking-tighter leading-tight">Which industry do you work in?</p>
                                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                                        {industries.map(sec => (
                                            <button key={sec.id} disabled={isAdvancing && selectingId !== sec.id} onClick={() => selectTraitAndAdvance('sector', sec.id)} className={`h-24 rounded-2xl text-[15px] font-black transition-all border-2 flex flex-col items-center justify-center gap-2 bg-gray-50 dark:bg-white/5 relative overflow-hidden leading-none px-1 shadow-sm ${traits.sector === sec.id ? 'border-indigo-500 bg-indigo-500/20 shadow-md ring-2 ring-indigo-500/10' : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-indigo-500/30 hover:scale-[1.02]'}`}>
                                                <span className="text-center">{sec.name}</span>
                                                {traits.sector === sec.id && <div className="absolute top-1.5 right-1.5"><Sparkles className="text-indigo-500 animate-pulse" size={14} /></div>}
                                            </button>
                                        ))}
                                    </div>
                                    <button onClick={skipStep} className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.3em] text-gray-500 hover:text-white transition-all mt-2">
                                        Skip this step <ArrowRight size={14} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 4 && (
                        <div className="flex flex-col items-center justify-center py-10 space-y-8 animate-in h-full text-center">
                            <div className="relative">
                                <div className="w-28 h-28 bg-primary-600/10 rounded-full flex items-center justify-center shadow-inner">
                                    <Loader2 className="animate-spin text-primary-600" size={48} />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h2 className="text-4xl font-black italic uppercase underline decoration-primary-600 decoration-8 underline-offset-4">Securing Access</h2>
                                <p className="text-sm font-black uppercase tracking-[0.5em] text-gray-500 animate-pulse">{backendMsg || "Joining the network privately..."}</p>
                            </div>
                        </div>
                    )}

                    {step === 7 && (
                        <div className="max-w-3xl mx-auto py-6 space-y-4 animate-in text-center flex flex-col justify-center">
                            <div className="space-y-3">
                                <div className="w-20 h-20 bg-green-500/10 rounded-[32px] flex items-center justify-center mx-auto mb-4 shadow-xl border-2 border-green-500/20 animate-pulse"><Ghost size={40} className="text-green-500" /></div>
                                {/* <h2 className="text-4xl sm:text-5xl font-black italic uppercase tracking-tighter text-gray-900 dark:text-white leading-[0.95]">Ready to Start?</h2> */}
                                <p className="text-gray-500 font-bold italic text-lg leading-relaxed max-w-lg mx-auto">
                                    Your <span className="text-primary-600 underline decoration-2 decoration-primary-600/40">Anonymous Profile</span> is ready. No one knows who you are, but your voice now carries weight.
                                </p>
                            </div>

                            <div className="p-6 bg-amber-400/5 dark:bg-primary-600/5 border-2 border-dashed border-primary-600/20 rounded-[40px] flex flex-col items-center gap-2 shadow-inner">
                                <div className="flex items-center gap-3 text-amber-500"><Coins size={24} /><span className="text-xs font-black uppercase tracking-widest text-primary-600">Your Onboarding Reward</span></div>
                                <div className="text-6xl font-black text-gray-900 dark:text-white tracking-tighter italic">+{coins || 70} <span className="text-amber-500 uppercase text-xs tracking-widest font-black non-italic">Hectar</span></div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-center gap-4 group cursor-pointer" onClick={() => setAcceptedTerms(!acceptedTerms)}>
                                    <div className={`w-6 h-6 rounded-lg border-2 transition-all flex items-center justify-center ${acceptedTerms ? 'bg-primary-600 border-primary-600 shadow-lg' : 'border-gray-300 dark:border-white/20'}`}>
                                        {acceptedTerms && <ShieldCheck size={14} className="text-white" />}
                                    </div>
                                    <label className="text-xs font-bold text-gray-500 hover:text-gray-300 transition-colors italic cursor-pointer">
                                        I accept the <span className="underline text-primary-600">Privacy Policy</span> & Anonymity Terms.
                                    </label>
                                </div>

                                <div className="relative w-full max-w-lg mx-auto mt-4">
                                    <SlideToConfirm
                                        onConfirm={handleSlideConfirm}
                                        idleLabel="Slide to Start"
                                        draggingLabel="Establishing Ghost Identity..."
                                        completeLabel="Identity Achieved!"
                                        ariaLabel="Slide to finish creating your Ghost identity, or press Enter"
                                        disabled={!acceptedTerms || loading || isSealing}
                                        trackHeightClass="h-[72px]"
                                        handleSize={60}
                                        threshold={0.99}
                                        idleIcon={Zap}
                                    />

                                    {/* Particle Burst Overlay */}
                                    {showConfetti && (
                                        <div className="absolute inset-0 pointer-events-none overflow-visible z-50">
                                            {[...Array(12)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="absolute left-1/2 top-1/2 w-2 h-2 bg-amber-400 rounded-sm animate-ping"
                                                    style={{
                                                        transform: `translate(${(Math.random() - 0.5) * 500}px, ${(Math.random() - 0.5) * 500}px) rotate(${Math.random() * 360}deg)`,
                                                        animationDuration: `${0.5 + Math.random()}s`,
                                                        opacity: 0
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 6 && (
                        <div className="h-full flex flex-col items-center justify-center p-8 text-center space-y-12 min-h-[480px]">
                            <div className="relative animate-in zoom-in spin-in-12 duration-1000 ease-out">
                                <div className="w-40 h-40 bg-gradient-to-br from-amber-400 to-amber-600 rounded-[48px] flex items-center justify-center mx-auto shadow-[0_30px_90px_rgba(245,158,11,0.6)] rotate-12 border-4 border-white/40"><ShieldCheck size={80} className="text-white drop-shadow-2xl" /></div>
                                <div className="absolute -top-6 -right-6 w-20 h-20 bg-white dark:bg-zinc-900 rounded-full flex items-center justify-center shadow-2xl animate-bounce border-4 border-amber-500/20"><Sparkles className="text-amber-500" size={32} /></div>
                            </div>
                            <div className="space-y-6">
                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                                    <h2 className="text-7xl font-black text-gray-900 dark:text-white italic uppercase tracking-tighter leading-none mb-2">Victory.</h2>
                                    <p className="text-2xl text-amber-500 font-black uppercase tracking-[0.4em] italic leading-none">Identity Secured</p>
                                </div>

                                <div className="inline-flex items-center gap-8 px-12 py-8 bg-amber-400/10 rounded-[48px] border-2 border-amber-400/30 shadow-[inset_0_2px_20px_rgba(245,158,11,0.2)] group animate-in zoom-in-90 delay-300 duration-700">
                                    <div className="relative">
                                        <Coins size={54} className="text-amber-500 group-hover:rotate-12 transition-transform duration-500" />
                                        <div className="absolute inset-0 bg-amber-400/40 blur-xl rounded-full animate-pulse" />
                                    </div>
                                    <div className="text-left leading-tight">
                                        <span className="block text-5xl font-black text-amber-500 italic transform skew-x-[-4deg]">+{coins} Hectar</span>
                                        <span className="text-2xs font-black uppercase opacity-60 text-amber-500 tracking-[0.2em]">Enlistment Bonus Claimed</span>
                                    </div>
                                </div>

                                <div className="text-gray-500 text-xs font-bold uppercase tracking-[0.5em] animate-pulse italic pt-4">
                                    Initializing Ghost Protocol...
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 10: DISCOVERY HUB (Wall Refactoring) */}
                    {step === 10 && (
                        <div className="flex flex-col items-center justify-center py-6 space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 h-full text-center">
                            <div className="space-y-4">
                                <div className="w-20 h-20 bg-amber-400/10 rounded-[32px] flex items-center justify-center mx-auto mb-2 border-2 border-amber-400/20">
                                    <ShieldAlert className="text-amber-500" size={32} />
                                </div>
                                <h2 className="text-4xl lg:text-5xl font-black italic uppercase tracking-tighter text-gray-900 dark:text-white leading-[0.85]">
                                    This Gate <br /><span className="text-secondary-600">is Locked.</span>
                                </h2>
                                <p className="text-gray-500 dark:text-gray-400 font-bold italic max-w-sm mx-auto leading-relaxed">
                                    Based on your established traits, you aren't eligible for this specific poll. But your time is valuable—explore these active missions instead:
                                </p>
                            </div>

                            <div className="w-full max-w-2xl grid gap-3">
                                {discoveryPolls.length > 0 ? discoveryPolls.map(poll => (
                                    <button 
                                        key={poll.id}
                                        onClick={() => {
                                            window.location.href = `/poll/${poll.id}`;
                                        }}
                                        className="group relative flex items-center justify-between p-5 bg-gray-50 dark:bg-white/5 border-2 border-transparent hover:border-primary-600 rounded-[32px] transition-all duration-300 text-left overflow-hidden sm:min-h-[100px]"
                                    >
                                        <div className="relative z-10 flex flex-col gap-1 pr-12">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xs font-black uppercase tracking-[0.2em] text-primary-600 bg-primary-600/10 px-2 py-0.5 rounded-full">{poll.category}</span>
                                                <span className="text-2xs font-bold text-gray-500 uppercase tracking-widest">{poll.totalVoters || 0} Citizens Voted</span>
                                            </div>
                                            <p className="font-bold text-gray-900 dark:text-white leading-tight italic line-clamp-2 md:text-lg">
                                                "{poll.question}"
                                            </p>
                                        </div>
                                        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-300 group-hover:text-primary-600 group-hover:translate-x-2 transition-all">
                                            <ChevronRight size={24} />
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-600/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                    </button>
                                )) : (
                                    <div className="py-12 bg-gray-50 dark:bg-white/5 rounded-[40px] border-2 border-dashed border-gray-200 dark:border-white/10">
                                        <Activity className="mx-auto text-gray-300 mb-4 animate-pulse" size={40} />
                                        <p className="text-2xs font-black uppercase tracking-[0.3em] text-gray-500">Scanning for new opportunities...</p>
                                    </div>
                                )}
                            </div>

                            <button onClick={() => setStep(0)} className="text-[11px] font-black uppercase tracking-[0.4em] text-gray-500 hover:text-primary-600 transition-colors pt-4">
                                ← Return to Identity Choice
                            </button>
                        </div>
                    )}
                </div>

                <div className="p-5 pt-0 flex justify-between items-center opacity-30 shrink-0">
                    <div className="flex items-center gap-2"><Shield size={10} className="text-green-500" /><span className="text-2xs font-black uppercase tracking-[0.4em] dark:text-white leading-none">Zk-Cryptographic Privacy Profile v4.0.2</span></div>
                    <div className="text-2xs font-black uppercase tracking-[0.4em] dark:text-white">Active Encryption 🤖</div>
                </div>
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};
